/**
 * @file Unsaved note draft state helpers for detail views.
 */

import { state } from '../../app/state/state.js';

const PENDING_NOTE_MATCH_WINDOW_MS = 2 * 60 * 1000;

function normalizeDraftText(text) {
    return typeof text === 'string' ? text : '';
}

function getPendingNotesState() {
    if (!state.detailPendingNotes || typeof state.detailPendingNotes !== 'object') {
        state.detailPendingNotes = {};
    }
    return state.detailPendingNotes;
}

/**
 * Returns the current unsaved note draft for an item.
 * @param {string|null} [itemId=state.selectedItemId] Detail item ID.
 * @returns {{itemId: string|null, text: string, hasText: boolean}}
 */
export function getDetailNoteDraft(itemId = state.selectedItemId) {
    const draft = state.detailNoteDraft || { itemId: null, text: '' };
    const draftText = draft.itemId === itemId ? normalizeDraftText(draft.text) : '';
    return {
        itemId: draft.itemId === itemId ? draft.itemId : itemId,
        text: draftText,
        hasText: draftText.trim().length > 0,
    };
}

/**
 * Stores the unsaved note draft for a detail item.
 * @param {string} itemId Detail item ID.
 * @param {string} text Draft note text.
 */
export function setDetailNoteDraft(itemId, text) {
    const draftText = normalizeDraftText(text);
    if (!itemId || draftText.trim().length === 0) {
        clearDetailNoteDraft(itemId);
        return;
    }

    state.detailNoteDraft = {
        itemId,
        text: draftText,
    };
}

/**
 * Clears the note draft when it belongs to the provided item.
 * @param {string|null} [itemId=state.selectedItemId] Detail item ID.
 */
export function clearDetailNoteDraft(itemId = state.selectedItemId) {
    if (!itemId || state.detailNoteDraft?.itemId === itemId) {
        state.detailNoteDraft = { itemId: null, text: '' };
    }
}

/**
 * Returns whether the active detail item has an unsaved note draft.
 * @param {string|null} [itemId=state.selectedItemId] Detail item ID.
 * @returns {boolean}
 */
export function hasDetailNoteDraft(itemId = state.selectedItemId) {
    return getDetailNoteDraft(itemId).hasText;
}

/**
 * Adds an optimistic pending note for an item.
 * @param {string} itemId Detail item ID.
 * @param {string} text Raw note text.
 * @param {string} createdBy Current user ID.
 * @returns {object|null} Pending note entry.
 */
export function addPendingDetailNote(itemId, text, createdBy) {
    const noteText = normalizeDraftText(text).trim();
    if (!itemId || !noteText) {return null;}

    const pendingNotes = getPendingNotesState();
    const createdAtMs = Date.now();
    const pendingNote = {
        id: `pending-note-${createdAtMs}-${Math.random().toString(36).slice(2)}`,
        itemId,
        content: noteText,
        createdBy,
        createdAtMs,
        isPending: true,
    };

    pendingNotes[itemId] = [
        pendingNote,
        ...(pendingNotes[itemId] || []),
    ];
    return pendingNote;
}

/**
 * Returns optimistic pending notes for an item.
 * @param {string|null} [itemId=state.selectedItemId] Detail item ID.
 * @returns {Array<object>} Pending notes.
 */
export function getPendingDetailNotes(itemId = state.selectedItemId) {
    if (!itemId) {return [];}
    return [...(getPendingNotesState()[itemId] || [])];
}

/**
 * Removes an optimistic pending note.
 * @param {string} itemId Detail item ID.
 * @param {string} pendingNoteId Pending note ID.
 */
export function removePendingDetailNote(itemId, pendingNoteId) {
    if (!itemId || !pendingNoteId) {return;}
    const pendingNotes = getPendingNotesState();
    pendingNotes[itemId] = (pendingNotes[itemId] || [])
        .filter(note => note.id !== pendingNoteId);
    if (pendingNotes[itemId].length === 0) {
        delete pendingNotes[itemId];
    }
}

/**
 * Reconciles optimistic notes against saved notes from Firestore.
 * @param {string} itemId Detail item ID.
 * @param {Array<object>} savedNotes Saved notes for the item.
 */
export function reconcilePendingDetailNotes(itemId, savedNotes) {
    if (!itemId || !Array.isArray(savedNotes)) {return;}

    const pendingNotes = getPendingNotesState();
    const itemPendingNotes = pendingNotes[itemId] || [];
    if (itemPendingNotes.length === 0) {return;}

    pendingNotes[itemId] = itemPendingNotes.filter((pendingNote) => {
        const hasSavedMatch = savedNotes.some((savedNote) => {
            if (savedNote.createdBy !== pendingNote.createdBy) {return false;}
            if (String(savedNote.content || '').trim() !== pendingNote.content) {
                return false;
            }
            const savedAtMs = savedNote.createdAt?.toMillis?.() ||
                savedNote.createdAt?.toDate?.()?.getTime?.() ||
                0;
            return savedAtMs === 0 ||
                Math.abs(savedAtMs - pendingNote.createdAtMs) <=
                    PENDING_NOTE_MATCH_WINDOW_MS;
        });
        return !hasSavedMatch;
    });

    if (pendingNotes[itemId].length === 0) {
        delete pendingNotes[itemId];
    }
}
